/**
 * Minimal, dependency-free Markdown renderer for Blueprint card bodies.
 *
 * Blueprint card bodies are grounded markdown (per the schema contract). Rather
 * than pull in a third-party renderer, this component supports the small subset
 * the concept uses — headings (`#`–`###`), unordered (`-`/`*`) and ordered
 * (`1.`) lists, GFM-style pipe tables, inline `**bold**`/`__bold__` and
 * `*italic*`/`_italic_`, and
 * inline grounding citations `[[src:<id>#L<a>-L<b>]]` — rendered as React nodes (never
 * `dangerouslySetInnerHTML`, so there is no injection surface for the PHI-bearing
 * content). Unrecognized lines render as paragraphs.
 *
 * Citation markers are parsed out of the prose so a cited fact reads cleanly,
 * with the citation becoming a subtle superscript hover affordance carrying the
 * source ref + line range. Section headings render with a real visual hierarchy
 * and lists render as semantic `<ul>`/`<ol>` so cards read as organized
 * sections. Nothing is logged (HIPAA): the tooltip shows only the citation ref
 * (an `SES-*`-style id + line range), never card content.
 *
 * The citation-aware inline parser (`**bold**`/`*italic*`/`[[src:...]]`) lives
 * in `./inlineCitation` (`renderInline`) so other text surfaces (e.g. the diff
 * viewer's word-diff runs) can reuse it instead of rendering raw
 * text; it also defensively strips a stray (un-bracketed) `src:...` mention or
 * bare UUID that leaks into free-form prose outside a well-formed
 * `[[src:...]]` marker — e.g. LLM chat output echoing a tool result's raw id —
 * so the human-readable-UI rule holds even when the source text didn't follow
 * the citation grammar.
 */
import React, { useMemo } from "react";
import { buildCitationLookup } from "./citationResolve";
import { renderInline } from "./inlineCitation";
import {
  HEADING_STYLES,
  HEADLINE_PARAGRAPH_CLASS,
  LIST_CLASSES,
  PARAGRAPH_CLASS,
  TABLE_ALIGN_CLASSES,
  TABLE_CLASSES,
} from "./proseStyles";
import type { SourceCitation } from "../../types";

/** The classification of a single source line. */
type LineKind = "h1" | "h2" | "h3" | "ul" | "ol" | "blank" | "p";

/** A classified source line with its content stripped of markers. */
interface ClassifiedLine {
  kind: LineKind;
  content: string;
}

/**
 * Classify one markdown line and strip its leading marker.
 *
 * @param raw - The raw source line.
 * @returns The line kind and its marker-stripped content.
 */
function classifyLine(raw: string): ClassifiedLine {
  const line = raw.trimEnd();
  if (line.trim() === "") return { kind: "blank", content: "" };
  if (line.startsWith("### ")) return { kind: "h3", content: line.slice(4) };
  if (line.startsWith("## ")) return { kind: "h2", content: line.slice(3) };
  if (line.startsWith("# ")) return { kind: "h1", content: line.slice(2) };
  const trimmed = line.trim();
  if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
    return { kind: "ul", content: trimmed.slice(2) };
  }
  const ordered = /^(\d+)\.\s+(.*)$/.exec(trimmed);
  if (ordered) return { kind: "ol", content: ordered[2] };
  return { kind: "p", content: line };
}

const HEADING_TAGS: Record<"h1" | "h2" | "h3", "h1" | "h2" | "h3"> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
};

/** A text block: a heading or a paragraph. */
interface TextBlock {
  kind: "h1" | "h2" | "h3" | "p";
  text: string;
}

/** A list block: an unordered or ordered list of items. */
interface ListBlock {
  kind: "ul" | "ol";
  items: string[];
}

/** A column's text alignment, parsed from its delimiter-row cell (e.g. `:---:`). */
type TableAlignment = "left" | "center" | "right";

/** A GitHub-style pipe table: a header row, per-column alignment, and body rows. */
interface TableBlock {
  kind: "table";
  header: string[];
  alignments: TableAlignment[];
  rows: string[][];
}

/** A block-level node assembled from one or more classified lines. */
type Block = TextBlock | ListBlock | TableBlock;

/** Narrow a block to a {@link ListBlock}. */
function isListBlock(block: Block): block is ListBlock {
  return block.kind === "ul" || block.kind === "ol";
}

/** Narrow a block to a {@link TableBlock}. */
function isTableBlock(block: Block): block is TableBlock {
  return block.kind === "table";
}

/** Split a pipe-delimited row into trimmed cells, dropping outer pipes. */
function splitTableRow(line: string): string[] {
  let trimmed = line.trim();
  if (trimmed.startsWith("|")) trimmed = trimmed.slice(1);
  if (trimmed.endsWith("|")) trimmed = trimmed.slice(0, -1);
  return trimmed.split("|").map((cell) => cell.trim());
}

/** Report whether a classified paragraph line looks like a pipe-table row. */
function isTableRowLine(content: string): boolean {
  return content.trim().includes("|");
}

const TABLE_SEPARATOR_CELL = /^:?-{1,}:?$/;

/** Resolve one delimiter cell (e.g. `:---:`) to its column alignment. */
function cellAlignment(cell: string): TableAlignment {
  const left = cell.startsWith(":");
  const right = cell.endsWith(":");
  if (left && right) return "center";
  if (right) return "right";
  return "left";
}

/** Parse a table's delimiter row into per-column alignments, or `null` if it isn't one. */
function parseSeparatorRow(content: string): TableAlignment[] | null {
  const cells = splitTableRow(content);
  if (!cells.every((cell) => TABLE_SEPARATOR_CELL.test(cell))) return null;
  return cells.map(cellAlignment);
}

/**
 * Match a GFM header + delimiter row pair at `lines[index:index+2]`, or `null`.
 *
 * Per GFM, the delimiter row must declare exactly as many columns as the header
 * row; a mismatched pair is not a table and both lines stay paragraphs.
 */
function matchTableHeader(
  lines: ClassifiedLine[],
  index: number,
): { header: string[]; alignments: TableAlignment[] } | null {
  const headerLine = lines[index];
  if (headerLine.kind !== "p" || !isTableRowLine(headerLine.content))
    return null;
  const separatorLine = lines[index + 1];
  if (!separatorLine || separatorLine.kind !== "p") return null;
  const alignments = parseSeparatorRow(separatorLine.content);
  if (alignments === null) return null;
  const header = splitTableRow(headerLine.content);
  if (header.length !== alignments.length) return null;
  return { header, alignments };
}

/** Try to parse a table starting at `lines[index]`; return the block + lines consumed. */
function tryStartTable(
  lines: ClassifiedLine[],
  index: number,
): { block: TableBlock; consumed: number } | null {
  const matched = matchTableHeader(lines, index);
  if (matched === null) return null;
  const rows: string[][] = [];
  let cursor = index + 2;
  while (cursor < lines.length) {
    const rowLine = lines[cursor];
    if (rowLine.kind !== "p" || !isTableRowLine(rowLine.content)) break;
    rows.push(splitTableRow(rowLine.content));
    cursor += 1;
  }
  return {
    block: {
      kind: "table",
      header: matched.header,
      alignments: matched.alignments,
      rows,
    },
    consumed: cursor - index,
  };
}

/**
 * Group classified lines into block-level nodes, merging adjacent list items.
 *
 * @param lines - The classified source lines.
 * @returns The assembled block nodes.
 */
function toBlocks(lines: ClassifiedLine[]): Block[] {
  const blocks: Block[] = [];
  let index = 0;
  while (index < lines.length) {
    const line = lines[index];
    if (line.kind === "blank") {
      index += 1;
      continue;
    }
    if (line.kind === "ul" || line.kind === "ol") {
      const last = blocks[blocks.length - 1];
      if (last && last.kind === line.kind && isListBlock(last)) {
        last.items.push(line.content);
      } else {
        blocks.push({ kind: line.kind, items: [line.content] });
      }
      index += 1;
      continue;
    }
    const table = tryStartTable(lines, index);
    if (table) {
      blocks.push(table.block);
      index += table.consumed;
      continue;
    }
    blocks.push({ kind: line.kind, text: line.content });
    index += 1;
  }
  return blocks;
}

/**
 * Render a list block as a semantic `<ul>`/`<ol>` with markers + item spacing.
 *
 * @param block - The list block.
 * @param index - The block's position (used as a stable key).
 * @param sourceTitles - Optional source-title lookup for citation resolution.
 * @returns The rendered list element.
 */
function renderListBlock(
  block: ListBlock,
  index: number,
  sourceTitles?: Record<string, string>,
  citations?: Map<string, SourceCitation>,
  cardTitles?: Record<string, string>,
  rawIdentifiers?: boolean,
): React.ReactElement {
  const items = block.items.map((item, i) => (
    <li key={i}>
      {renderInline(item, sourceTitles, citations, cardTitles, rawIdentifiers)}
    </li>
  ));
  return block.kind === "ol" ? (
    <ol key={index} className={LIST_CLASSES.ol}>
      {items}
    </ol>
  ) : (
    <ul key={index} className={LIST_CLASSES.ul}>
      {items}
    </ul>
  );
}

/**
 * Render a table block as a semantic `<table>` with header + body rows.
 *
 * @param block - The table block.
 * @param index - The block's position (used as a stable key).
 * @param sourceTitles - Optional source-title lookup for citation resolution.
 * @returns The rendered table element.
 */
function renderTableBlock(
  block: TableBlock,
  index: number,
  sourceTitles?: Record<string, string>,
  citations?: Map<string, SourceCitation>,
  cardTitles?: Record<string, string>,
  rawIdentifiers?: boolean,
): React.ReactElement {
  const alignClass = (col: number): string =>
    TABLE_ALIGN_CLASSES[block.alignments[col] ?? "left"];
  return (
    <div key={index} className={TABLE_CLASSES.wrapper}>
      <table className={TABLE_CLASSES.table}>
        <thead>
          <tr>
            {block.header.map((cell, col) => (
              <th
                key={col}
                className={`${TABLE_CLASSES.th} ${alignClass(col)}`}
              >
                {renderInline(
                  cell,
                  sourceTitles,
                  citations,
                  cardTitles,
                  rawIdentifiers,
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, r) => (
            <tr key={r}>
              {row.map((cell, col) => (
                <td
                  key={col}
                  className={`${TABLE_CLASSES.td} ${alignClass(col)}`}
                >
                  {renderInline(
                    cell,
                    sourceTitles,
                    citations,
                    cardTitles,
                    rawIdentifiers,
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Matches a standalone bold headline line (e.g. `**Evidence:**`, `**Caregiver Report:**`). */
const BOLD_HEADLINE_PATTERN = /^\s*\*\*[^*]+\*\*:?\s*$/;

/**
 * Render a single block node to a React element.
 *
 * Future seam: a richer renderer (e.g. a time-series chart for the
 * longitudinal-metrics card) would plug in here as a new `Block` variant —
 * extend `classifyLine`/`toBlocks` to recognize a fenced ```chart block and
 * add a `renderChartBlock` branch beside `renderListBlock`. Markdown stays the
 * stored truth; the chart would be a richer *view* of the same data. Charts are
 * intentionally NOT built yet.
 *
 * @param block - The block to render.
 * @param index - The block's position (used as a stable key).
 * @param sourceTitles - Optional source-title lookup for citation resolution.
 * @returns The rendered element.
 */
function renderBlock(
  block: Block,
  index: number,
  sourceTitles?: Record<string, string>,
  citations?: Map<string, SourceCitation>,
  cardTitles?: Record<string, string>,
  rawIdentifiers?: boolean,
): React.ReactElement {
  if (isListBlock(block)) {
    return renderListBlock(
      block,
      index,
      sourceTitles,
      citations,
      cardTitles,
      rawIdentifiers,
    );
  }
  if (isTableBlock(block)) {
    return renderTableBlock(
      block,
      index,
      sourceTitles,
      citations,
      cardTitles,
      rawIdentifiers,
    );
  }
  if (block.kind === "p") {
    const isHeadline = BOLD_HEADLINE_PATTERN.test(block.text);
    return (
      <p
        key={index}
        className={isHeadline ? HEADLINE_PARAGRAPH_CLASS : PARAGRAPH_CLASS}
      >
        {renderInline(
          block.text,
          sourceTitles,
          citations,
          cardTitles,
          rawIdentifiers,
        )}
      </p>
    );
  }
  const Tag = HEADING_TAGS[block.kind];
  return (
    <Tag key={index} className={HEADING_STYLES[block.kind]}>
      {renderInline(
        block.text,
        sourceTitles,
        citations,
        cardTitles,
        rawIdentifiers,
      )}
    </Tag>
  );
}

/** Props for {@link Markdown}. */
interface MarkdownProps {
  /** The markdown source to render. */
  source: string;
  /**
   * Optional `{ "<source id>": "<title>" }` lookup. When provided, inline
   * citations resolve to the human source title (e.g. "Source: Visit 1
   * Transcript, lines 3–7") instead of the neutral "Source · lines 3–7"; the
   * raw source id is never shown either way. Accepts `null` so callers can pass
   * an API field (e.g. `EntityPage.source_titles`) straight through.
   */
  sourceTitles?: Record<string, string> | null;
  /**
   * Optional structured citation provenance (`frontmatter.citations`). When
   * provided, an inline citation whose ref matches gains a click-popover with
   * the verbatim cited excerpt + a "Go to source" navigate button; absent
   * entries degrade to the hover label only.
   */
  citations?: SourceCitation[] | null;
  /**
   * Optional `{ "<card id>": "<name>" }` lookup so an inline `[[inf:...]]`
   * inference marker can name the Blueprint cards it was drawn from. Without
   * it the marker still renders (the fact-vs-interpretation distinction is the
   * point) but lists no cards.
   */
  cardTitles?: Record<string, string> | null;
  /**
   * When true, bypass defensive stripping of stray UUIDs and raw `src:` tokens,
   * preserving verbatim text for viewing raw source documents.
   */
  rawIdentifiers?: boolean;
}

/**
 * Render a markdown string as styled React nodes.
 *
 * @param props - The markdown source, an optional source-title lookup, and
 *   optional structured citation provenance for the excerpt/navigate popover.
 * @returns The rendered prose, or an empty-state note when blank.
 */
export const Markdown: React.FC<MarkdownProps> = ({
  source,
  sourceTitles,
  citations,
  cardTitles,
  rawIdentifiers = false,
}) => {
  const lookup = useMemo(() => buildCitationLookup(citations), [citations]);
  // Coerce a `null` lookup (e.g. an absent `EntityPage.source_titles`) to
  // `undefined` so the resolver helpers below keep their single optional shape.
  const titles = sourceTitles ?? undefined;
  const cards = cardTitles ?? undefined;
  // Defensive: `source` is typed `string`, but section content comes from a
  // dynamically-typed version API that can hand back a non-string (a structured
  // block, a null) when an older version is restored. `String(...)` makes the
  // `.trim()`/`.split()` below total instead of fatal — without this guard a
  // restored malformed section crashed the whole tab (white screen).
  const text = typeof source === "string" ? source : String(source ?? "");
  if (text.trim() === "") {
    return <p className="text-slate-400 italic">No content recorded yet.</p>;
  }
  const blocks = toBlocks(text.split("\n").map(classifyLine));
  return (
    <>
      {blocks.map((block, i) =>
        renderBlock(block, i, titles, lookup, cards, rawIdentifiers),
      )}
    </>
  );
};

export default Markdown;
